import {
  describePasswordProblems,
  friendlyError,
  PASSWORD_HINT,
  passwordProblems,
  useAuth,
} from '@hobbytools/platform';
import { Banner, Button, TextField } from '@hobbytools/ui';
import { type FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { AuthLayout, linkClass } from './AuthLayout';

/** Signed out: request a reset link. Signed in (arrived from the link): choose a new password. */
export function ResetPassword() {
  const { user, isLoading, requestPasswordReset, updatePassword } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async (fn: () => Promise<void>) => {
    setError(null);
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  };

  if (isLoading) return <AuthLayout title="Reset your password">{null}</AuthLayout>;

  if (user) {
    const submit = (e: FormEvent) => {
      e.preventDefault();
      run(async () => {
        await updatePassword(password);
        navigate('/', { replace: true });
      });
    };
    return (
      <AuthLayout title="Choose a new password" lead={`Signed in as ${user.email}.`}>
        {error ? <Banner tone="danger">{error}</Banner> : null}
        <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
          <TextField
            label="New password"
            type="password"
            autoComplete="new-password"
            description={PASSWORD_HINT}
            errorMessage={
              (password.length > 0 && describePasswordProblems(passwordProblems(password))) || undefined
            }
            isRequired
            value={password}
            onChange={setPassword}
          />
          <Button type="submit" isDisabled={busy || passwordProblems(password).length > 0}>
            {busy ? 'Saving…' : 'Save password'}
          </Button>
        </form>
      </AuthLayout>
    );
  }

  if (sentTo) {
    return (
      <AuthLayout title="Check your email">
        <Banner tone="success">
          If an account exists for <strong>{sentTo}</strong>, a reset link is on its way.
        </Banner>
        <Link className={linkClass} to="/sign-in">
          Back to sign in
        </Link>
      </AuthLayout>
    );
  }

  const submit = (e: FormEvent) => {
    e.preventDefault();
    run(async () => {
      await requestPasswordReset(email.trim());
      setSentTo(email.trim());
    });
  };
  return (
    <AuthLayout
      title="Reset your password"
      lead="We'll email you a link to choose a new one."
      footer={
        <Link className={linkClass} to="/sign-in">
          Back to sign in
        </Link>
      }
    >
      {error ? <Banner tone="danger">{error}</Banner> : null}
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <TextField
          label="Email"
          type="email"
          autoComplete="email"
          isRequired
          value={email}
          onChange={setEmail}
        />
        <Button type="submit" isDisabled={busy || !email}>
          {busy ? 'Sending…' : 'Send reset link'}
        </Button>
      </form>
    </AuthLayout>
  );
}
